/**
 * PublicSharePage.jsx — client-facing RotoTube gallery at /m/:token.
 *
 * Unauthenticated: mounted ABOVE the app's auth gate. Fetches a token-scoped
 * share (no cookie) and renders a Roto Flight Media–branded gallery. All media
 * resolves through token-scoped backend endpoints, so no source credentials are
 * ever exposed. Handles not-found (404) and revoked/expired (410) states.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Chip, IconButton } from '@mui/material';
import {
  PlayArrow as PlayIcon, Download as DownloadIcon, VideocamOff as OfflineIcon,
} from '@mui/icons-material';
import PlayerModal from '../components/rototube/PlayerModal';
import { YT, fmtDuration } from '../components/rototube/ytTheme';

function useShare(token) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/media/share/${encodeURIComponent(token)}`, { credentials: 'omit' });
        if (!alive) return;
        if (res.status === 404) return setState({ loading: false, data: null, error: 'not_found' });
        if (res.status === 410) {
          const body = await res.json().catch(() => ({}));
          return setState({ loading: false, data: null, error: body.error === 'expired' ? 'expired' : 'revoked' });
        }
        if (!res.ok) return setState({ loading: false, data: null, error: 'error' });
        setState({ loading: false, data: await res.json(), error: null });
      } catch {
        if (alive) setState({ loading: false, data: null, error: 'error' });
      }
    })();
    return () => { alive = false; };
  }, [token]);
  return state;
}

function Brand() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: YT.accent }} />
      <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.08em', fontSize: '1.35rem', color: YT.text }}>
        ROTO FLIGHT MEDIA
      </Typography>
    </Box>
  );
}

function Message({ icon, title, sub }) {
  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: YT.textSecondary, textAlign: 'center', px: 3 }}>
      {icon}
      <Typography sx={{ color: YT.text, fontSize: '1.15rem', fontWeight: 500 }}>{title}</Typography>
      {sub && <Typography variant="body2">{sub}</Typography>}
    </Box>
  );
}

function ShareCard({ item, onPlay }) {
  return (
    <Box className="rt-pcard" onClick={() => onPlay(item)} sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: '12px', overflow: 'hidden', bgcolor: '#181818', border: `1px solid ${YT.border}` }}>
        <Box component="img" src={item.thumb} loading="lazy" alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 150ms', bgcolor: 'rgba(0,0,0,0.28)', '.rt-pcard:hover &': { opacity: 1 } }}>
          <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlayIcon sx={{ fontSize: 30, color: '#fff' }} />
          </Box>
        </Box>
        {item.kind !== 'image' && item.duration ? (
          <Chip label={fmtDuration(item.duration)} size="small" sx={{ position: 'absolute', bottom: 8, right: 8, height: 20, bgcolor: 'rgba(0,0,0,0.82)', color: '#fff', fontSize: '0.7rem', borderRadius: '4px', '& .MuiChip-label': { px: 0.75 } }} />
        ) : null}
        {item.download && (
          <IconButton
            size="small" component="a" href={item.download} download
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute', top: 6, right: 6, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)',
              opacity: 0, transition: 'opacity 120ms', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              '.rt-pcard:hover &': { opacity: 1 },
              // Touch devices have no hover — keep the button visible.
              '@media (hover: none)': { opacity: 1, p: '10px' },
            }}
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: YT.text, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.title}</Typography>
    </Box>
  );
}

export default function PublicSharePage() {
  const { token } = useParams();
  const { loading, data, error } = useShare(token);
  const [player, setPlayer] = useState(null);

  const openPlayer = (item) => setPlayer({ kind: item.kind, src: item.play, title: item.title, download: item.download });

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: YT.bg, color: YT.text }}>
      <Box sx={{ borderBottom: `1px solid ${YT.border}`, px: { xs: 2, md: 4 }, py: 1.75, position: 'sticky', top: 0, bgcolor: 'rgba(15,15,15,0.92)', backdropFilter: 'blur(8px)', zIndex: 5 }}>
        <Brand />
      </Box>

      <Box sx={{ maxWidth: 1500, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
        {loading ? (
          <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : error === 'not_found' ? (
          <Message icon={<OfflineIcon sx={{ fontSize: 44, opacity: 0.5 }} />} title="Link not found" sub="This share link is invalid or has been removed." />
        ) : error === 'revoked' ? (
          <Message icon={<OfflineIcon sx={{ fontSize: 44, opacity: 0.5 }} />} title="Link revoked" sub="This share is no longer available. Please contact Roto Flight Media." />
        ) : error === 'expired' ? (
          <Message icon={<OfflineIcon sx={{ fontSize: 44, opacity: 0.5 }} />} title="Link expired" sub="This share link has expired. Please contact Roto Flight Media for a fresh link." />
        ) : error ? (
          <Message icon={<OfflineIcon sx={{ fontSize: 44, opacity: 0.5 }} />} title="Something went wrong" sub="Please try again in a moment." />
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' }, fontWeight: 600 }}>{data.title || 'Shared media'}</Typography>
              <Typography sx={{ color: YT.textSecondary, mt: 0.5 }}>
                {data.clientName ? `Prepared for ${data.clientName} · ` : ''}{data.itemCount} {data.itemCount === 1 ? 'item' : 'items'}
              </Typography>
            </Box>
            {!data.items?.length ? (
              <Message icon={<OfflineIcon sx={{ fontSize: 44, opacity: 0.5 }} />} title="Nothing here yet" />
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                {data.items.map((item) => <ShareCard key={item.id} item={item} onPlay={openPlayer} />)}
              </Box>
            )}
          </>
        )}
      </Box>

      <PlayerModal item={player} onClose={() => setPlayer(null)} actions={player?.download ? { downloadHref: player.download } : null} />
    </Box>
  );
}
